<template>
  <CtSearchContainerSkeleton
    v-if="isAwaitingFirstResult"
    :show-search-box="showSearchBox"
    :show-breadcrumbs="!!categoryId"
    :show-search-result-count="isOnSearchPage"
  />
  <CtSearchError
    v-else-if="status === 'error'"
    class="mx-5 my-10 lg:mx-auto"
    @retry="refresh()"
  />
  <div
    v-else
    class="ct-product-listing"
  >
    <CtSearchResultsTop
      class="mt-4 mb-2 lg:mb-12"
      :sort-by="filterState.sortBy"
      :active-filter-count="activeFilterCount"
      :show-breadcrumbs="!!categoryId"
      :search-result-count="isOnSearchPage ? total : undefined"
      :search-term="filterState.query"
      :description="description"
      @sort="setSort"
    />

    <div class="flex gap-2 items-start">
      <CtFilterModal
        class="lg:w-80"
        :product-count="total"
        @clear-all="clearAll"
      >
        <div
          v-if="showSearchBox"
          class="mb-4"
        >
          <form
            class="relative h-11"
            @submit.prevent
          >
            <input
              type="search"
              class="w-full h-full pl-3 pr-20 border border-EXPONDO-primary-20 rounded bg-EXPONDO-white
                text-EXPONDO-black placeholder:text-EXPONDO-grey-60 appearance-none
                [&::-webkit-search-cancel-button]:hidden"
              :maxlength="MAX_LENGTH_FOR_SEARCH_INPUT"
              :placeholder="$t('Searchbox placeholder')"
              :value="localSearchQuery"
              :aria-label="$t('Searchbox placeholder')"
              @input="onSearchInput(($event.target as HTMLInputElement).value)"
            >
            <button
              v-if="localSearchQuery"
              type="button"
              class="absolute right-14 top-1/2 -translate-y-1/2 flex"
              :aria-label="$t('Close')"
              @click="onSearchInput('')"
            >
              <span class="iconify svg--close text-EXPONDO-primary-60 pr-0.5" />
            </button>
            <button
              type="submit"
              class="absolute top-0 right-0 w-12 h-full flex justify-center items-center rounded-r"
              :aria-label="$t('Submit search')"
            >
              <span class="iconify svg--search text-lg text-EXPONDO-primary" />
            </button>
          </form>
        </div>
        <CtCurrentRefinements
          class="mb-5"
          :current-refinements="allCurrentRefinements"
          @clear-all="clearAll"
        />
        <div
          v-if="!categoryId && categoryTree.length"
          class="mb-5"
        >
          <CustomHierarchicalMenuList
            :items="categoryTree"
            :refine="setCategory"
          />
        </div>
        <div
          v-if="categoryId && !isNewCategoryTree"
          class="mb-5"
        >
          <SideCategories />
        </div>
        <CtRefinementList
          v-for="filter in CT_SEARCH_FILTERS"
          :key="filter.name"
          class="mb-5"
          :filter="filter"
          :items="filterItems[filter.name]"
          :price-range="priceRange"
          :current-price-refinement="currentPriceRefinement"
          @refine="(value) => refineRefinementList(filter, value)"
          @refine-price="refinePrice"
          @refine-rating="(value) => setRating(Number(value))"
        />
      </CtFilterModal>

      <LoadingOverlay
        class="flex-grow min-w-0"
        :loading="isLoading"
      >
        <CtProductListing
          v-if="hasResults"
          :products="products"
          :query="filterState.query"
          :offset="result?.offset ?? 0"
          @product-listing-shown="$emit('product-listing-shown', $event)"
        />
        <CtNoResults
          v-else
          :search-term="filterState.query"
        />
        <div
          v-if="total > 0"
          class="w-full flex justify-center items-center my-10 px-10"
        >
          <CtPagination
            :current-page="filterState.page"
            :total-pages="totalPages"
            :create-url="getPageUrl"
            @page-change="setPage"
          />
        </div>
      </LoadingOverlay>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MIN_CHARS_FOR_SEARCH, MAX_LENGTH_FOR_SEARCH_INPUT } from '~~/constants/typesense';
import {
  CT_SEARCH_FACET_NAME,
  CT_SEARCH_FILTERS,
  CT_SEARCH_FILTER_KIND,
} from '~~/constants/commercetoolsProductSearch';
import type { CtSearchFilter, CtSearchFilterName } from '~~/constants/commercetoolsProductSearch';
import type { CtSearchRefinementListFilterDefinition } from '~~/types/commercetools/productSearch';
import { useCtProductSearch } from '~~/app/composables/useCtProductSearch';
import { useProductListingFilters } from '~~/app/composables/useProductListingFilters';
import type {
  ProductListingFilterState,
  ProductListingRefinementList,
} from '~~/types/productListing/ProductListingFilterState';
import type { CtCategoryTreeItem, CtRefinementListItem } from '~~/types/productListing/CtSearchProductsResult';
import { mapCtFacetToRefinementItems } from '~~/helpers/productListing/mapCtFacetToRefinementItems';
import { mapCtTagsFacetToRefinementItems } from '~~/helpers/productListing/mapCtTagsFacetToRefinementItems';
import { mapCtRatingFacetToItems } from '~~/helpers/productListing/mapCtRatingFacetToItems';
import { getCtPriceFacetRange } from '~~/helpers/productListing/getCtPriceFacetRange';
import { omitPriceBoundsAtRangeEdges } from '~~/helpers/productListing/omitPriceBoundsAtRangeEdges';
import type { ValueRange } from '~~/types/ValueRange';
import type { CtProductCard } from '~~/types/productListing/CtProductCard';
import {
  getProductListingLastPage,
  isProductListingPageInvalid,
  isProductListingPageReachable,
} from '~~/helpers/productListing/productListingPagination';
import { STATUS_BAD_REQUEST, STATUS_RESOURCE_NOT_FOUND, STATUS_SERVICE_UNAVAILABLE } from '~~/constants/http';
import { preloadProductCard } from './CtProductListing.vue';

// CT version of TypesenseContainer: `categoryId` for category pages, `showSearchBox` for /search,
// `defaultFilters`, `syncWithUrl` (enableStateMapping) and `description` for CMS listings.
defineOptions({ name: 'CtSearchContainer' });

const props = withDefaults(defineProps<{
  categoryId?: string;
  defaultFilters?: ProductListingRefinementList;
  showSearchBox?: boolean;
  syncWithUrl?: boolean;
  description?: string;
}>(), {
  categoryId: undefined,
  defaultFilters: undefined,
  showSearchBox: false,
  syncWithUrl: true,
  description: '',
});

const emit = defineEmits<{
  (event: 'product-listing-shown', products: CtProductCard[]): void;
  (event: 'page-changed', page: number): void;
}>();

const { defaultFilters, syncWithUrl } = toRefs(props);
const event = import.meta.server ? useRequestEvent() : null;
const containerId = useId();
const {
  filterState,
  toggleRefinement,
  setPriceRange,
  setRating,
  setCategory,
  setSort,
  setQuery,
  setPage,
  getPageUrl,
  clearAll,
  currentRefinements,
} = useProductListingFilters({ defaultFilters: defaultFilters.value, syncWithUrl: syncWithUrl.value });
const { isNewCategoryTree } = useCategories();
const { isOnSearchPage } = useIsPage();

const getListingKey = () => {

  if (props.categoryId) {

    return `category-${props.categoryId}`;

  }

  if (props.defaultFilters) {

    return `cms-${JSON.stringify(props.defaultFilters)}`;

  }

  return 'search';

};

// The id keeps several listings on one page (e.g. CMS blocks with the same filters) apart.
const stateKey = `ct-search-container-${getListingKey()}-${containerId}`;
const { search } = useCtProductSearch();

watch(() => filterState.value.page, (page) => {

  emit('page-changed', page);

}, { immediate: true });

// filterState.query lags behind typing (debounce + router round-trip); binding the input to it
// would reset the field on any re-render. lastDispatchedQuery separates our own updates from
// external ones (back/forward), which still have to sync the input.
const localSearchQuery = ref(filterState.value.query);
const lastDispatchedQuery = ref(filterState.value.query);

watch(() => filterState.value.query, (newQuery) => {

  if (newQuery === lastDispatchedQuery.value) {

    return;

  }

  localSearchQuery.value = newQuery;
  lastDispatchedQuery.value = newQuery;

});

const searchReachablePage = (state: ProductListingFilterState, signal: AbortSignal) => {

  if (!isProductListingPageReachable(state)) {

    return null;

  }

  return search({ categoryId: props.categoryId, filterState: state, signal });

};

// The endpoint answers 400 when it or CT rejects the request, e.g. a hand-made filter URL.
const isRejectedSearch = (error: unknown) =>
  error instanceof Error && 'statusCode' in error && error.statusCode === STATUS_BAD_REQUEST;

// Anything else is an outage: crawlers retry a 503 later instead of dropping the page.
const failServerResponse = (error: unknown): never => {

  if (event) {

    setResponseStatus(event, isRejectedSearch(error) ? STATUS_BAD_REQUEST : STATUS_SERVICE_UNAVAILABLE);

  }

  throw error;

};

// Lazy, so a client navigation shows the skeleton at once instead of keeping the previous page until the search ends.
const { data: result, status, refresh } = useLazyAsyncData(stateKey, async (_nuxtApp, { signal }) => {

  const state = filterState.value;
  // The skeleton stays until the cards can render too (only the first client search loads them).
  const [response] = await Promise.all([
    searchReachablePage(state, signal),
    import.meta.client ? preloadProductCard() : null,
  ]).catch(failServerResponse);
  const { page, hitsPerPage } = state;
  const total = response?.total ?? 0;

  if (import.meta.server && event && isProductListingPageInvalid({ page, hitsPerPage, total })) {

    setResponseStatus(event, STATUS_RESOURCE_NOT_FOUND);

  }

  return response;

}, { default: () => null });

// The `watch` option would queue the new search until the previous one finishes; refresh() aborts it instead.
watch([filterState, () => props.categoryId], () => refresh());

const isLoading = computed(() => status.value === 'pending');
// The listing mounts only with its first results: it sends its analytics events on mount.
const isAwaitingFirstResult = computed(() => !result.value && (status.value === 'idle' || isLoading.value));
const products = computed(() => result.value?.products || []);
const total = computed(() => result.value?.total || 0);
// Not `total`: a page past the last one has a total but no products.
const hasResults = computed(() => products.value.length > 0);
const totalPages = computed(() =>
  getProductListingLastPage({ total: total.value, hitsPerPage: filterState.value.hitsPerPage }));

const findFacet = (name: string) => result.value?.facets?.find(facet => facet.name === name);

const getRefinementListItemsMapper = (filter: CtSearchRefinementListFilterDefinition) =>
  (filter.isJsonArray ? mapCtTagsFacetToRefinementItems : mapCtFacetToRefinementItems);

const getFilterItems = (filter: CtSearchFilter): CtRefinementListItem[] => {

  const facet = findFacet(filter.name);

  switch (filter.kind) {

    case CT_SEARCH_FILTER_KIND.REFINEMENT_LIST:

      return getRefinementListItemsMapper(filter)(facet, filterState.value.refinementList[filter.name]);

    case CT_SEARCH_FILTER_KIND.RATING:

      return mapCtRatingFacetToItems(facet, filterState.value.ratingMenu.avgRating);

    case CT_SEARCH_FILTER_KIND.PRICE:

      return [];

  }

};

type CtSearchFilterItems = Partial<Record<CtSearchFilterName, CtRefinementListItem[]>>;

const filterItems = computed(() => CT_SEARCH_FILTERS.reduce<CtSearchFilterItems>(
  (items, filter) => ({ ...items, [filter.name]: getFilterItems(filter) }),
  {},
));

const refineRefinementList = (filter: CtSearchFilter, value: string) => {

  if (filter.kind === CT_SEARCH_FILTER_KIND.REFINEMENT_LIST) {

    toggleRefinement(filter.name, value);

  }

};

const priceRange = computed(() => getCtPriceFacetRange(findFacet(CT_SEARCH_FACET_NAME.PRICE)));
const refinePrice = (range: ValueRange) =>
  setPriceRange(omitPriceBoundsAtRangeEdges({ range, bounds: priceRange.value }));
const currentPriceRefinement = computed(() => ({
  min: filterState.value.range.price?.min ?? null,
  max: filterState.value.range.price?.max ?? null,
}));

// Built on the server (buildCtCategoryTree), unlike the other facets.
const categoryTree = computed(() => result.value?.categoryTree ?? []);

const findCategoryLabel = (items: CtCategoryTreeItem[], value: string): string | undefined => {

  for (const item of items) {

    if (item.value === value) {

      return item.label;

    }

    const label = findCategoryLabel(item.data, value);

    if (label) {

      return label;

    }

  }

  return undefined;

};

const allCurrentRefinements = computed(() => {

  const categoryId = filterState.value.category;

  if (!categoryId) {

    return currentRefinements.value;

  }

  return [
    ...currentRefinements.value,
    {
      attribute: 'category',
      label: findCategoryLabel(categoryTree.value, categoryId) ?? categoryId,
      value: categoryId,
      remove: () => setCategory(undefined),
    },
  ];

});

// Counted per filter, as Typesense's filter button does: a price range is one filter.
const activeFilterCount = computed(() =>
  new Set(allCurrentRefinements.value.map(refinement => refinement.attribute)).size);

const searchInputTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

const onSearchInput = (value: string) => {

  localSearchQuery.value = value;

  if (searchInputTimeout.value) {

    clearTimeout(searchInputTimeout.value);

  }

  if (value && value.length < MIN_CHARS_FOR_SEARCH) {

    return;

  }

  searchInputTimeout.value = setTimeout(() => {

    lastDispatchedQuery.value = value;
    setQuery(value);

  }, 300);

};
</script>
